import { FormType, MembershipValidityPeriod } from "@simplyk/common";

import { useFormV2Context } from "../../context/FormV2Context";

import { ThemedItemBlockProps } from "./types";

import { useLocaleField } from "@/features/FormV2/helpers/useLocaleField";
import { ThemedAuctionInputProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedAuctionInput";
import { ThemedBidBadgeProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedBidBadge";
import { ThemedBidStatusProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedBidStatus";
import { ThemedGroupTicketCaptionProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedGroupTicketCaption";
import { ThemedItemPriceProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedItemPrice";
import { ThemedItemTitleProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedItemTitle";
import { ThemedMembershipRenewalInfoProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedMembershipRenewalInfo";
import { MultipleImageDisplayProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedMultipleImageDisplay/ThemedResponsiveMultipleImageDisplay";
import { ThemedRemainingItemsInfoProps } from "@/features/FormV2/sharedComponents/ThemedItemBlock/ThemedRemainingItemsInfo";

export interface ThemedItemBlockSlotVisibility {
  imageDisplay: MultipleImageDisplayProps | undefined;
  title: ThemedItemTitleProps;
  description: string;
  price: ThemedItemPriceProps;
  priceCaption: ThemedBidStatusProps | undefined;
  bidBadge: ThemedBidBadgeProps | undefined;
  remainingItemsInfo: ThemedRemainingItemsInfoProps | undefined;
  renewalInfo: ThemedMembershipRenewalInfoProps | undefined;
  amountInput: ThemedAuctionInputProps | undefined;
  groupTicketCaption: ThemedGroupTicketCaptionProps | undefined;
}

export function useThemedItemBlock(
  props: Omit<ThemedItemBlockProps, "id" | "active" | "isSoldOut" | "highlightEarlyBirdStatus" | "amountInput">
): ThemedItemBlockSlotVisibility {
  const {
    rate,
    formCurrency,
    highestBidsMapByRateId,
    commandBidsRateIdsMap,
    hasAlreadyBid,
    maximumPurchasableTicketsForRateAndEventCapacity,
    ticketingIsClosed = false,
    indexInDisplayedRates,
    isPreview,
    onBidChange,
    questions,
    form,
    formCategory,
    isAuction = false,
    itemType = "ticket",
  } = props;
  const { isP2PLinkedEvent } = useFormV2Context(FormType.Ticketing);

  // --- Hooks and helpers ---
  const getLocaleField = useLocaleField();

  // --- Image Display ---
  const showImageDisplay = Boolean(rate.photoUrls && rate.photoUrls.length > 0);

  // --- Price ---
  let priceAmount = rate.amount;
  if (isAuction) {
    const highestBid = highestBidsMapByRateId?.[rate.id]?.amount;
    priceAmount = highestBid ?? rate.amount;
  }

  // --- Price Caption ---
  const showPriceCaption = isAuction;

  // --- Bid Status ---
  const rateId = rate.id;
  const userHasBid = Boolean(commandBidsRateIdsMap?.[rateId]);
  const someoneHasBid = Boolean(highestBidsMapByRateId?.[rateId]);
  const currentBidAmount = commandBidsRateIdsMap?.[rateId]?.amount;
  const highestBidAmount = highestBidsMapByRateId?.[rateId]?.amount;
  const isWinning = userHasBid && currentBidAmount === highestBidAmount;
  const isOutbid = userHasBid && currentBidAmount !== highestBidAmount;
  const priceBidStatus = isWinning ? "yourBid" : isOutbid ? "newTopBid" : someoneHasBid ? "currentBid" : "startingBid";

  // --- Bid Badge ---
  const hasABid = currentBidAmount !== undefined && currentBidAmount !== null;
  const isWinningBid = hasABid && currentBidAmount === highestBidAmount; // TODO: check if same as isWinning
  const showBidBadge = isAuction && hasAlreadyBid && hasABid;
  const bidBadgeStatus = isWinningBid ? "isWinning" : "isOutbid";

  // --- Remaining Items Info ---

  const showRemainingItemsInfo = !(
    itemType === "membership" ||
    isP2PLinkedEvent ||
    ticketingIsClosed ||
    !maximumPurchasableTicketsForRateAndEventCapacity ||
    maximumPurchasableTicketsForRateAndEventCapacity <= 0 ||
    maximumPurchasableTicketsForRateAndEventCapacity === Infinity ||
    maximumPurchasableTicketsForRateAndEventCapacity > 10
  );

  // --- Renewal Info ---
  const showRenewalInfo = itemType === "membership";

  // --- Group Ticket Caption ---
  const showGroupTicketCaption = Boolean(rate.attendeesByTicket && rate.attendeesByTicket > 1);

  // --- Auction Input ---
  const highestBid = highestBidsMapByRateId?.[rate.id]?.amount;
  const rateHasABid = Boolean(highestBid);
  const currentBid = Math.max(highestBid ?? -Infinity, rate.amount);
  const currentBidIncrement = (rateHasABid ? rate.bidIncrement : 0) || 0;
  const minimumBid = currentBid + currentBidIncrement;

  return {
    imageDisplay: showImageDisplay
      ? {
          purchasedNumber: 0,
          title: getLocaleField(rate.rateFields)?.title ?? "",
          photoUrls: rate.photoUrls ?? [],
        }
      : undefined,
    title: {
      children: getLocaleField(rate.rateFields)?.title ?? "",
    },
    description: getLocaleField(rate.rateFields)?.description ?? "",
    price: {
      price: priceAmount,
      formCurrency,
    },
    priceCaption: showPriceCaption
      ? {
          status: priceBidStatus,
        }
      : undefined,
    bidBadge: showBidBadge
      ? {
          status: bidBadgeStatus,
        }
      : undefined,
    remainingItemsInfo: showRemainingItemsInfo
      ? {
          remainingItemCount: maximumPurchasableTicketsForRateAndEventCapacity,
        }
      : undefined,
    renewalInfo: showRenewalInfo
      ? {
          validityPeriod: rate.membershipValidityPeriod as MembershipValidityPeriod,
          renewalDate: rate.membershipValidityPeriodAtDate ? new Date(rate.membershipValidityPeriodAtDate) : undefined,
        }
      : undefined,
    amountInput: isAuction
      ? {
          rate: { ...rate, minimumBid },
          indexInDisplayedRates,
          ticketingIsClosed,
          form,
          formCurrency,
          questions,
          isPreview: isPreview ?? false,
          onBidChange,
        }
      : undefined,
    groupTicketCaption: showGroupTicketCaption
      ? {
          count: rate.attendeesByTicket || 1,
          formCategory,
        }
      : undefined,
  };
}
